import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    TextField,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { createSecretWithKBToken, deleteSecret, listSecrets } from "../API/KB/Files";
import { encryptKBItem } from "../App/cryptoUtils";
import { getKB } from "../API/KB/KBAPI";
import { useConfirm } from "../Components/ConfirmContext";

export const insecureSecretWarning = `Your secret is too simple, so we cannot mask it if it accidentally appears in the function output. To add an extra layer of security, ensure it's at least 8 characters, including uppercase, lowercase letters, and numbers.\n\nDo you wish to continue with this secret?`;

export function isSecretComplex(secret) {
    // Define the complexity rules
    const minLength = 8;
    const hasUppercase = /[A-Z]/;
    const hasLowercase = /[a-z]/;
    const hasDigit = /[0-9]/;
    //const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;

    // Check the password against the rules
    return (
        (secret.length >= minLength &&
            hasUppercase.test(secret) &&
            hasLowercase.test(secret) &&
            hasDigit.test(secret)) ||
        new Set(secret).size > minLength
    );
}

export function SecretsList({ open, handleClose, kbId, setKBData, KBData }) {
    const [secrets, setSecrets] = useState([]);
    const [newSecretName, setNewSecretName] = useState('');
    const [newSecretValue, setNewSecretValue] = useState('');
    const [loading, setLoading] = useState(false); // Existing loading state for fetching secrets
    const [adding, setAdding] = useState(false);   // New state for adding secrets
    const [deletingIds, setDeletingIds] = useState(new Set()); // New state for deleting secrets
    const [errors, setErrors] = useState({});
    const showConfirm = useConfirm();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await listSecrets();
                setSecrets(data?.data || []);
            } catch (error) {
                console.error("Error fetching secrets:", error);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchData();
        }
    }, [open, kbId]);

    // Make sure code editor has a fresh secrets list to execute functions
    const updateKBData = async () => {
        try {
            let { createdAt, updatedAt, userId, ...KB } = await getKB();
            setKBData(KB);
        } catch (error) {
            console.error("Error updating KB data:", error);
        }
    };

    const handleCreateSecret = async (e) => {
        e.preventDefault(); // Prevent form from refreshing the page

        // Validate form fields
        const newErrors = {};
        if (!newSecretName) newErrors.newSecretName = "Secret name is required.";
        if (!newSecretValue) newErrors.newSecretValue = "Secret value is required.";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (!isSecretComplex(newSecretValue)) {
            const confirmed = await showConfirm({ title: 'Insecure Secret Warning', content: insecureSecretWarning});
            if (!confirmed) return;
        }

        try {
            setAdding(true); // Start adding loader
            await createSecretWithKBToken(newSecretName, encryptKBItem(newSecretValue, kbId));
            const data = await listSecrets();
            await updateKBData();

            setSecrets(data?.data || []);
            setNewSecretName('');
            setNewSecretValue('');
            setErrors({});
        } catch (error) {
            // Handle error (optional)
            console.error("Error adding secret:", error);
        } finally {
            setAdding(false); // Stop adding loader
        }
    };

    if (!open) return null;

    return (
        <div>
            <Dialog open={!!open} onClose={handleClose} maxWidth='md' fullWidth>
                <DialogTitle>Secrets List</DialogTitle>
                <DialogContent>
                    <Box style={{ overflow: 'auto', marginBottom: '20px' }}>
                        <List>
                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" padding={2}>
                                    <CircularProgress />
                                </Box>
                            ) : secrets.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="No secrets available." />
                                </ListItem>
                            ) : (
                                secrets.map((secret, index) => (
                                    <ListItem key={index} style={{ paddingRight: '70px' }}>
                                        <ListItemText primary={secret.name} style={{ marginRight: 50 }} />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={async () => {
                                                    const confirmed = await showConfirm({
                                                        title: 'Delete Confirmation',
                                                        content: `Are you sure you want to delete "${secret.name}"?`,
                                                    });
                                                    if (!confirmed) return;

                                                    try {
                                                        setDeletingIds(prev => new Set(prev).add(secret.name)); // Start deleting loader for this secret
                                                        await deleteSecret(secret.name);
                                                        await updateKBData();
                                                        const data = await listSecrets();
                                                        setSecrets(data?.data || []);
                                                    } catch (error) {
                                                        // Handle error (optional)
                                                        console.error("Error deleting secret:", error);
                                                    } finally {
                                                        setDeletingIds(prev => {
                                                            const newSet = new Set(prev);
                                                            newSet.delete(secret.name);
                                                            return newSet;
                                                        }); // Stop deleting loader for this secret
                                                    }
                                                }}
                                                disabled={deletingIds.has(secret.name) || adding}
                                            >
                                                {deletingIds.has(secret.name) ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <DeleteIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Box>
                    <form onSubmit={handleCreateSecret}>
                        <TextField
                            label="Secret Name"
                            value={newSecretName}
                            onChange={(e) => setNewSecretName(e.target.value)}
                            fullWidth
                            margin="normal"
                            error={!!errors.newSecretName}
                            helperText={errors.newSecretName}
                            disabled={adding}
                        />
                        <TextField
                            label="Secret Value"
                            type="password"
                            value={newSecretValue}
                            onChange={(e) => setNewSecretValue(e.target.value)}
                            fullWidth
                            margin="normal"
                            error={!!errors.newSecretValue}
                            helperText={errors.newSecretValue}
                            disabled={adding}
                        />
                        <Box display="flex" alignItems="center" marginTop="20px">
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={adding}
                                startIcon={adding && <CircularProgress size={20} color="inherit" />}
                            >
                                {adding ? "Adding..." : "Add Secret"}
                            </Button>
                            <Button
                                onClick={handleClose}
                                color="primary"
                                variant="outlined"
                                style={{ marginLeft: 10 }}
                                disabled={adding}
                            >
                                Close
                            </Button>
                        </Box>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}